import type { ProviderCreator } from '@wix/thunderbolt-ioc'
import type { ViewerAPIProvider, IViewerManagerImplementor } from 'thunderbolt-viewer-manager-types'
import type { DSCarmi } from 'thunderbolt-ds-carmi-root'
import type { ViewerAPI } from '@wix/viewer-manager-interface'
import { ViewerManagerApiImplementor } from 'thunderbolt-viewer-manager-types'
import { createDefaultViewerAPI } from './defaultViewerAPI'
import _ from 'lodash'
import { TbDebugSymbol } from 'feature-debug'
import type { IDebugDS } from 'ds-feature-debug-ds'
import { NO_INTERCEPTOR_SET, INFLATED_COMP_ID_INTERCEPTORS } from './sharedPartsCompIdsInterceptors'
import type { Experiments } from '@wix/thunderbolt-symbols'
import { CarmiInstance, ExperimentsSymbol } from '@wix/thunderbolt-symbols'

const FUNCTION = 'function'

const isFunction = (val: any): boolean => typeof val === FUNCTION
const isPromise = (val: any): boolean => val && isFunction(val.then)

export const ViewerManagerProvider: ProviderCreator<ViewerAPIProvider> = (container) => {
	const experiments: Experiments = container.get(ExperimentsSymbol)
	const carmiInstance: DSCarmi = container.get(CarmiInstance)

	const dontInflateMasterPage =
		carmiInstance.viewerModel.site.isResponsive &&
		experiments['specs.thunderbolt.DontInflateMasterPage'] &&
		!experiments['specs.thunderbolt.doNotInflateSharedParts']

	const defaultApis = createDefaultViewerAPI()

	const getInterceptedFn = (fn: Function, namespace: string, key: string) => {
		if (NO_INTERCEPTOR_SET.has(`${namespace}.${key}`)) {
			return fn
		}

		const interceptor = INFLATED_COMP_ID_INTERCEPTORS[`${namespace}.${key}`]

		if (isFunction(fn) && !isFunction(interceptor)) {
			throw new Error(
				`Method "${namespace}.${key}" has no interceptor in sharedPartsCompIdsInterceptors.ts - please, take care of inflated compIds`
			)
		}

		return interceptor(fn)
	}

	const measuredFn = (debugApi: IDebugDS, fn: Function, name: string): Function => {
		return (...args: Array<any>) => {
			const start = Date.now()
			const entry: any = {
				duration: -1,
				name,
				args,
				res: undefined,
			}
			debugApi.viewerAPIRecorder.add(entry)
			const res = fn(...args)
			entry.res = res
			if (isPromise(res)) {
				res.then((r: any) => {
					entry.res = r
					entry.duration = Date.now() - start
				}).catch((e: any) => {
					entry.res = { rejectedPromiseError: e }
					entry.duration = Date.now() - start
				})
			} else {
				entry.duration = Date.now() - start
			}
			return res
		}
	}

	return async () => ({
		getViewerAPI() {
			const allImplementors = container.getAll<
				IViewerManagerImplementor<keyof ViewerAPI, keyof ViewerAPI[keyof ViewerAPI]>
			>(ViewerManagerApiImplementor)

			let debugApi: IDebugDS | null = null
			try {
				debugApi = container.get(TbDebugSymbol)
			} catch (e) {
				// TODO handle thrown error
			}

			return allImplementors.reduce<ViewerAPI>(
				(apis, implementor) =>
					_.keys(implementor).reduce(
						(acc, namespace) => ({
							...acc,
							[namespace]: {
								...acc[namespace as keyof ViewerAPI],
								..._.mapValues(implementor[namespace as keyof ViewerAPI], (fn: any, key: string) => {
									if (!isFunction(fn)) {
										return
									}

									const exposedFn = dontInflateMasterPage ? getInterceptedFn(fn, namespace, key) : fn

									return debugApi && isFunction(exposedFn)
										? measuredFn(debugApi, exposedFn, `${namespace}.${key}`)
										: exposedFn
								}),
							},
						}),
						apis
					),
				defaultApis as ViewerAPI
			)
		},
	})
}
